import {Injectable, NgZone, signal} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Client, IMessage, StompSubscription} from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import {BehaviorSubject, Observable, Subject, Subscription, of, throwError} from 'rxjs';
import {catchError, map, take, tap} from 'rxjs/operators';

import {environment} from '../../../environments/environment';
import {ApiFeedbackService} from '@/core/api-feedback.service';
import {AuthService} from '@/core/auth/auth.service';
import {SecureStorage} from '@/core/storage/secure-storage';
import {BaseResponse, Page} from '@/shared/models';
import {
  EMPTY_NOTIFICATION_SUMMARY,
  NotificationItem,
  NotificationListQuery,
  NotificationRealtimeEvent,
  NotificationSummary,
  NotificationSummaryQuery
} from './notification-center.model';

@Injectable({providedIn: 'root'})
export class NotificationCenterService {
  private readonly baseUrl = `${environment.apiBase}/crm/notifications`;
  private readonly unreadCountQuery: NotificationSummaryQuery = {};
  readonly unreadCount = signal(0);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly eventsSubject = new Subject<NotificationRealtimeEvent>();

  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  readonly events$ = this.eventsSubject.asObservable();

  private client?: Client;
  private subscription?: StompSubscription;
  private started = false;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private authSubscription?: Subscription;
  private currentSocketToken: string | null = null;
  private lastSyncEventAt = 0;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private storage: SecureStorage,
    private feedback: ApiFeedbackService,
    private zone: NgZone
  ) {
    this.authSubscription = this.auth.accessToken$.subscribe(token => {
      if (!this.started || token === this.currentSocketToken) {
        return;
      }

      if (!token) {
        this.currentSocketToken = null;
        this.teardownRealtimeClient();
        this.scheduleReconnect();
        return;
      }

      this.currentSocketToken = token;
      this.restartRealtimeConnection();
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.handleWindowFocus);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  start(): void {
    if (!this.started) {
      this.started = true;
      this.refreshUnreadCount(true);
    }

    if (!this.client?.active) {
      this.connectRealtime();
    }
  }

  disconnect(): void {
    this.started = false;
    this.currentSocketToken = null;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.teardownRealtimeClient();
  }

  loadNotifications(
    query: NotificationListQuery = {},
    silent = false
  ): Observable<Page<NotificationItem>> {
    return this.http
      .get<BaseResponse<Page<NotificationItem>>>(this.baseUrl, {
        params: this.buildParams(query)
      })
      .pipe(
        map(response => this.normalizePage(response.data)),
        catchError(error => {
          if (!silent) {
            this.feedback.showError(this.resolveErrorMessage(error, 'No se pudieron cargar las notificaciones'));
          }
          return throwError(() => error);
        })
      );
  }

  loadSummary(
    query: NotificationSummaryQuery = {},
    silent = false
  ): Observable<NotificationSummary> {
    return this.http
      .get<BaseResponse<NotificationSummary>>(`${this.baseUrl}/summary`, {
        params: this.buildParams(query)
      })
      .pipe(
        map(response => ({
          total: Number(response.data?.total ?? 0),
          unread: Number(response.data?.unread ?? 0),
          categories: response.data?.categories ?? []
        })),
        catchError(error => {
          if (!silent) {
            this.feedback.showError(this.resolveErrorMessage(error, 'No se pudo cargar el resumen de notificaciones'));
          }
          return throwError(() => error);
        })
      );
  }

  refreshUnreadCount(silent = true): void {
    this.loadSummary(this.unreadCountQuery, silent)
      .pipe(
        take(1),
        catchError(() => of(EMPTY_NOTIFICATION_SUMMARY))
      )
      .subscribe(summary => {
        this.setUnreadCount(Number(summary.unread ?? 0));
      });
  }

  markViewed(notificationUuid: string, silent = true): Observable<void> {
    return this.http
      .patch<BaseResponse<string>>(`${this.baseUrl}/${notificationUuid}/viewed`, {})
      .pipe(
        tap(() => {
          this.refreshUnreadCount(true);
        }),
        map(() => void 0),
        catchError(error => {
          if (!silent) {
            this.feedback.showError(this.resolveErrorMessage(error, 'No se pudo actualizar la notificación'));
          }
          return throwError(() => error);
        })
      );
  }

  private connectRealtime(): void {
    const token = this.getRealtimeToken();
    if (!token) {
      this.scheduleReconnect();
      return;
    }

    if (this.client?.active) {
      return;
    }

    this.currentSocketToken = token;

    this.client = new Client({
      webSocketFactory: () => new SockJS(this.buildRealtimeUrl(this.getRealtimeToken() ?? token)),
      connectHeaders: {
        Authorization: `Bearer ${token}`
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = undefined;
        }
        this.subscription?.unsubscribe();
        this.subscription = this.client?.subscribe('/user/queue/notifications', (message: IMessage) => {
          this.handleRealtimeMessage(message);
        });
        this.syncFromServer('notification.connected', true);
      },
      onDisconnect: () => {
        this.subscription = undefined;
      },
      onStompError: frame => {
        console.error('Notification broker error:', frame.headers['message']);
      },
      onWebSocketError: event => {
        console.error('Notification WebSocket error:', event);
      },
      onWebSocketClose: () => {
        this.subscription = undefined;
      }
    });

    this.client.activate();
  }

  private restartRealtimeConnection(): void {
    this.refreshUnreadCount(true);
    this.teardownRealtimeClient(() => {
      if (this.started) {
        this.connectRealtime();
      }
    });
  }

  private handleRealtimeMessage(message: IMessage): void {
    try {
      const payload = this.normalizeRealtimeEvent(JSON.parse(message.body));
      this.zone.run(() => {
        this.applyRealtimeUnreadCount(payload);
        this.eventsSubject.next(payload);
      });
    } catch (error) {
      console.error('Error parsing notification event:', error);
    }
  }

  private normalizeRealtimeEvent(payload: Partial<NotificationRealtimeEvent>): NotificationRealtimeEvent {
    return {
      eventType: String(payload?.eventType ?? ''),
      notificationUuid: String(payload?.notificationUuid ?? ''),
      occurredAt: String(payload?.occurredAt ?? ''),
      unreadCount: this.parseUnreadCount(payload?.unreadCount)
    };
  }

  private applyRealtimeUnreadCount(event: NotificationRealtimeEvent): void {
    if (event.unreadCount !== null && event.unreadCount !== undefined) {
      this.setUnreadCount(event.unreadCount);
      return;
    }

    this.refreshUnreadCount(true);
  }

  private parseUnreadCount(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const unreadCount = Number(value);
    return Number.isFinite(unreadCount) ? unreadCount : null;
  }

  private setUnreadCount(value: number): void {
    const nextValue = Math.max(0, Number(value || 0));
    this.unreadCount.set(nextValue);
    this.unreadCountSubject.next(nextValue);
  }

  private emitSyncEvent(eventType: string, unreadCount: number | null = this.unreadCount()): void {
    const now = Date.now();
    if (now - this.lastSyncEventAt < 300) {
      return;
    }

    this.lastSyncEventAt = now;
    this.zone.run(() => {
      this.eventsSubject.next({
        eventType,
        notificationUuid: '',
        occurredAt: new Date().toISOString(),
        unreadCount
      });
    });
  }

  private syncFromServer(eventType: string, alwaysEmit = false): void {
    if (!this.started) {
      return;
    }

    const previousUnreadCount = this.unreadCount();
    this.loadSummary(this.unreadCountQuery, true)
      .pipe(
        take(1),
        catchError(() => of(EMPTY_NOTIFICATION_SUMMARY))
      )
      .subscribe(summary => {
        const nextUnreadCount = Number(summary.unread ?? 0);
        this.setUnreadCount(nextUnreadCount);
        if (alwaysEmit || previousUnreadCount !== nextUnreadCount) {
          this.emitSyncEvent(eventType, nextUnreadCount);
        }
      });
  }

  private readonly handleWindowFocus = (): void => {
    this.syncFromServer('notification.focused', true);
  };

  private readonly handleVisibilityChange = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      this.syncFromServer('notification.visible', true);
    }
  };

  private scheduleReconnect(): void {
    if (!this.started || this.reconnectTimer) {
      return;
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      if (this.started && !this.client?.active) {
        this.connectRealtime();
      }
    }, 1000);
  }

  private teardownRealtimeClient(onAfterDeactivate?: () => void): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;

    if (!this.client) {
      onAfterDeactivate?.();
      return;
    }

    const currentClient = this.client;
    this.client = undefined;

    void currentClient.deactivate().finally(() => {
      onAfterDeactivate?.();
    });
  }

  private getRealtimeToken(): string | null {
    return this.auth.getAccessToken() || this.storage.get('access') || this.storage.get('token');
  }

  private buildRealtimeUrl(token: string): string {
    const apiBase = environment.apiBase.replace(/\/api$/, '');
    return `${apiBase}/ws?token=${token}`;
  }

  private buildParams(query: NotificationListQuery | NotificationSummaryQuery): HttpParams {
    let params = new HttpParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      params = params.set(key, String(value));
    });

    return params;
  }

  private normalizePage(page: Page<NotificationItem> | null | undefined): Page<NotificationItem> {
    return {
      content: page?.content ?? [],
      pageable: page?.pageable ?? {
        pageNumber: 0,
        pageSize: 0,
        offset: 0
      },
      totalPages: Number(page?.totalPages ?? 0),
      totalElements: Number(page?.totalElements ?? 0),
      first: Boolean(page?.first ?? true),
      last: Boolean(page?.last ?? true),
      size: Number(page?.size ?? 0),
      number: Number(page?.number ?? 0),
      numberOfElements: Number(page?.numberOfElements ?? 0),
      empty: Boolean(page?.empty ?? true)
    };
  }

  private resolveErrorMessage(error: any, fallback: string): string {
    return error?.error?.error
      ?? error?.error?.message
      ?? error?.message
      ?? fallback;
  }
}
